import { Typography } from '@packages/ui/shared';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

import { DECISION_MODAL_WIDTH } from '../ssm5.constants';
import { ssm5TextStyles } from '../ssm5.styles';
import { Ssm5Modal } from './ssm5-modal';

/** Props for {@link Ssm5ConnectionLostModal}. */
export type Ssm5ConnectionLostModalProps = {
    /** Called when the technician leaves the lost session behind to connect again. */
    onRestart: () => void;
    /** Called when the technician keeps reviewing the last reading in disconnected mode. */
    onStay: () => void;
    /** Whether the modal is visible. */
    show: boolean;
};

/** The likely causes of a lost connection, in the order the design lists them. */
const CAUSES = ['power', 'dst', 'cloudConnector'] as const;

const useStyles = createUseStyles({
    causes: {
        margin: 0,
        paddingLeft: 24,
    },
});

/**
 * Modal telling the technician that the SSM5 connection to the vehicle was lost, what usually causes
 * it, and letting them stay in disconnected mode or restart the connection.
 *
 * @param props - {@link Ssm5ConnectionLostModalProps}
 * @returns the modal
 */
export function Ssm5ConnectionLostModal({ onRestart, onStay, show }: Ssm5ConnectionLostModalProps) {
    const classes = useStyles();
    const { t } = useTranslation();

    const actions = {
        primary: { onPress: onRestart, title: t('ssm5:connectionLost.restart') },
        secondary: { onPress: onStay, title: t('ssm5:connectionLost.stay') },
    };

    return (
        <Ssm5Modal
            actions={actions}
            look="card"
            onHide={onStay}
            show={show}
            title={t('ssm5:connectionLost.title')}
            width={DECISION_MODAL_WIDTH}
        >
            <Typography style={ssm5TextStyles.body} variant="default">
                {t('ssm5:connectionLost.description')}
            </Typography>
            <ul className={classes.causes}>
                {CAUSES.map((cause) => (
                    <li key={cause}>
                        <Typography style={ssm5TextStyles.body} variant="default">
                            {t(`ssm5:connectionLost.causes.${cause}`)}
                        </Typography>
                    </li>
                ))}
            </ul>
            <Typography style={ssm5TextStyles.body} variant="default">
                {t('ssm5:connectionLost.hint')}
            </Typography>
        </Ssm5Modal>
    );
}
